import { Component, OnInit } from '@angular/core';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';

/**
 * MSAL redirect-bridge landing page (see MSAL.js v5 COOP support).
 *
 * Entra's login pages enforce Cross-Origin-Opener-Policy, which severs the
 * window handle between the app and the sign-in popup — the opener can no
 * longer read the popup's URL or even see it close. Every sign-in therefore
 * lands here: for the popup flow this page relays the auth response to the
 * main window over BroadcastChannel and the popup closes; for the full-page
 * redirect fallback it caches the response and navigates back to the page
 * that started the login. Sign-out never reaches here — it is local to the
 * app and does not involve Entra.
 *
 * Must stay free of MSAL API calls, guards, and interceptors, and must be
 * registered as a SPA redirect URI in each environment's app registration.
 */
@Component({
  selector: 'app-redirect',
  standalone: true,
  template: `
    @if (failed) {
      <p>
        Sign-in could not be completed in this window. You can close it and try
        again.
      </p>
    } @else {
      <p>Processing authentication...</p>
    }
  `,
})
export class RedirectComponent implements OnInit {
  /** Set when the handoff fails and this window is still open to say so. */
  protected failed = false;

  ngOnInit(): void {
    broadcastResponseToMainFrame().catch((error: unknown) => {
      if (!this.hasAuthResponseInUrl()) {
        // Nothing to relay, so this is not a live auth handoff — someone
        // opened the bridge URL directly, or reloaded it after the response
        // was consumed. Send the window on to the app rather than leaving a
        // technical page on screen. A full load is required: the app shell
        // stands itself down on this route, so an in-app hop cannot recover.
        window.location.replace('/login');
        return;
      }

      // A response was present but could not be relayed. This window is
      // almost certainly a sign-in popup, and navigating a popup into the app
      // would show a second copy of the login page while the opener waits out
      // its timeout. Close it instead, and explain if the browser refuses to
      // close a window it did not open.
      console.error(
        'Failed to relay the auth response to the main window:',
        error,
      );
      this.failed = true;
      window.close();
    });
  }

  /** Whether the URL still carries an auth response for the bridge to relay. */
  private hasAuthResponseInUrl(): boolean {
    return (
      window.location.hash.length > 1 || window.location.search.length > 1
    );
  }
}
